import { FC } from 'react'
import { ReactComponent as LocationIcon } from 'src/assets/svg/locationPin.svg'
import { ReactComponent as StarIcon } from 'src/assets/svg/ri-star-fill.svg'
import { ReactComponent as TrendUpIcon } from 'src/assets/svg/trend-up.svg'
import { ReactComponent as SadIcon } from 'src/assets/svg/mingcute_sad-line.svg'
import { ReactComponent as SmileIcon } from 'src/assets/svg/uil_smile.svg'
import { Skeleton } from 'src/components/Skeleton'
import {
  PerformanceCardKind,
  PerformanceLocation,
} from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import { TruncatedText } from 'src/pages/ReviewAnalysis/TruncatedText'
import styles from './performanceLocations.module.scss'

// From performanceLocations.module.scss: .card is 167.66x153 with radius 12,
// laid out in a 16px-gap .cardsWrapper. Each .groupContainer is at least
// 220px tall and both stretch to the taller one, so Best and Worst always
// match; the skeletons stand in for the card contents while loading, so the
// state reads as "cards are coming" instead of an unexplained empty panel.
const CARD_WIDTH = 167.66
const CARD_HEIGHT = 153
const CARD_RADIUS = 12
const CARD_SKELETON_COUNT = 2

interface PerformanceLocationsProps {
  bestLocations?: PerformanceLocation[]
  isLoading?: boolean
  worstLocations?: PerformanceLocation[]
}

const KIND_LABELS: Record<PerformanceCardKind, string> = {
  lowestRated: 'Lowest rating',
  mostNegative: 'Most negative sentiment',
  mostPositive: 'Most positive sentiment',
  topRated: 'Top rated',
}

// The redesigned card: rank, name and what it is ranked for on the left;
// average rating and the sentiment share on the right.
const RankedCard: FC<{ isWorst: boolean; loc: PerformanceLocation }> = ({
  isWorst,
  loc,
}) => {
  const sentimentPct = isWorst ? loc.negativePct : loc.positivePct
  const FaceIcon = isWorst ? SadIcon : SmileIcon
  return (
    <div className={styles.rankedCard}>
      {loc.rank != null && (
        <span
          className={`${styles.rankBadge} ${isWorst ? styles.rankBadgeWorst : ''}`}
        >
          #{loc.rank}
        </span>
      )}
      <div className={styles.pinTile}>
        <LocationIcon />
      </div>
      <div className={styles.rankedMain}>
        <TruncatedText className={styles.rankedName} text={loc.name} />
        {loc.kind && (
          <span
            className={`${styles.kindPill} ${isWorst ? styles.kindPillWorst : ''}`}
          >
            {KIND_LABELS[loc.kind]}
          </span>
        )}
      </div>
      <div className={styles.rankedStats}>
        <div className={styles.rankedRating}>
          <span className={styles.rankedRatingValue}>
            {loc.rating?.toFixed(1) ?? '—'}
            <StarIcon className={styles.rankedStar} />
          </span>
          <span className={styles.rankedCaption}>Average rating</span>
        </div>
        <div className={styles.rankedSentiment}>
          <span
            className={`${styles.faceIcon} ${isWorst ? styles.faceIconWorst : ''}`}
          >
            <FaceIcon />
          </span>
          <div>
            <span
              className={`${styles.sentimentPct} ${isWorst ? styles.sentimentPctWorst : ''}`}
            >
              {sentimentPct ?? '—'}%
            </span>
            <span className={styles.rankedCaption}>
              {isWorst ? 'Negative sentiment' : 'Positive sentiment'}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

const LocationGroup: FC<{
  cardClass: string
  emptyText?: string
  isLoading: boolean
  isWorst?: boolean
  locations: PerformanceLocation[]
  subtitle: string
  title: string
}> = ({
  cardClass,
  emptyText,
  isLoading,
  isWorst = false,
  locations,
  subtitle,
  title,
}) => {
  // Newer backends name each card's kind and send rank, rating and both
  // shares; the redesigned row needs all of them.
  if (locations.length > 0 && locations.every((loc) => loc.kind)) {
    return (
      <div className={`${styles.groupContainer} ${styles.groupRanked}`}>
        <div>
          <h3 className={styles.groupHeader}>{title}</h3>
          <p className={styles.groupSubtitle}>{subtitle}</p>
        </div>
        <div className={styles.rankedList}>
          {locations.map((loc) => (
            <RankedCard isWorst={isWorst} key={loc.kind} loc={loc} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.groupContainer}>
      <div>
        <h3 className={styles.groupHeader}>{title}</h3>
        <p className={styles.groupSubtitle}>{subtitle}</p>
      </div>
      {!isLoading && locations.length === 0 && emptyText && (
        <p className={styles.emptyText}>{emptyText}</p>
      )}
      <div className={styles.cardsWrapper}>
        {isLoading &&
          Array.from({ length: CARD_SKELETON_COUNT }).map((_, idx) => (
            <Skeleton
              height={CARD_HEIGHT}
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              radius={CARD_RADIUS}
              width={CARD_WIDTH}
            />
          ))}
        {locations.map((loc, idx) => (
          <div className={`${styles.card} ${cardClass}`} key={idx}>
            <div className={styles.cardTop}>
              <div className={styles.cardLocation}>
                <div className={styles.locationIcon}>
                  <LocationIcon />
                </div>
                <TruncatedText
                  className={styles.locationName}
                  text={loc.name}
                />
              </div>
            </div>
            <div className={styles.cardBottom}>
              <p className={styles.cardLabel}>{loc.label}</p>
              <div className={styles.cardValueRow}>
                {loc.isRating && (
                  <div className={styles.starIcon}>
                    <StarIcon />
                  </div>
                )}
                <p className={styles.cardValue}>{loc.value}</p>
                {!loc.isRating && loc.text && (
                  <p className={styles.cardValueText}>{loc.text}</p>
                )}
                {!loc.text && (
                  <div className={styles.trendIcon}>
                    <TrendUpIcon
                      style={{
                        color: loc.trend === 'down' ? '#F97316' : '#10B981',
                        transform:
                          loc.trend === 'down' ? 'rotate(180deg)' : undefined,
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export const PerformanceLocations: FC<PerformanceLocationsProps> = ({
  bestLocations = [],
  isLoading = false,
  worstLocations = [],
}) => {
  // First load only — a refetch (e.g. a date-range change) keeps the cards that
  // are already on screen rather than flashing back to placeholders.
  const isInitialLoading =
    isLoading && bestLocations.length === 0 && worstLocations.length === 0

  return (
    <div className={styles.container}>
      <LocationGroup
        cardClass={styles.cardPositive}
        isLoading={isInitialLoading}
        locations={bestLocations}
        subtitle="Locations with the highest rating or positive sentiment"
        title="Best Performing Locations"
      />
      <LocationGroup
        cardClass={styles.cardNegative}
        // sm.back leaves worst empty when a single location is in scope — it
        // would only repeat the best card ("Lowest Rated 5.0").
        emptyText={
          bestLocations.length > 0
            ? 'Only one location in this view, so there is nothing to compare.'
            : undefined
        }
        isLoading={isInitialLoading}
        isWorst
        locations={worstLocations}
        subtitle="Locations that need attention based on lower ratings or negative sentiment"
        title="Worst Performing Locations"
      />
    </div>
  )
}
